import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { LoadingButton } from "@mui/lab";
import { Button, IconButton, Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { AuthContext } from "../../../contexts/AuthContext";
import { DashboardContext } from "../../../contexts/DashboardContext";
import { DashboardsContext } from '../../../contexts/DashboardsContext';
import { getUserEmail } from "../../../helpers/authUser";
import { updateDashboardFunc } from "../../../lib/helper";
import { scrollbarStyle } from "../../../shared/dashboard";
import theme from '../../../theme';
import { Dashboard, DashboardNote, DashboardNoteType, RefreshDataEnum } from "../../../types/files";
import { NotesContent } from "../../modals/dashboard-notes/DashboardNoteDialog";
import { CommentaryTitle } from "../dashboard-notes/Commentary";

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        width: 450,
        maxWidth: 500,
        height: 'fit-content',
        padding: 16,
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: 'all 300ms ease-in-out',
    },
    container: {
        display: 'block',
        width: '100%',
        height: '100%',
        padding: 8,
        borderRadius: 12,
        transition: 'all 0.2s ease',
        breakInside: 'avoid',
        overflowX: 'hidden',
        overflowY: 'auto',
        ...scrollbarStyle,
    },
    title: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-all',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: '600',
        color: '#0f172a',
    },
    inputField: {
        width: '100%',
        "& .MuiOutlinedInput-root": {
            width: '100%',
            padding: 'unset',
            "& > textarea": {
                fontFamily: 'Lato',
                fontSize: '0.9rem',
                color: '#475569',
                padding: 'unset',
            },
        },
    },
    icon: {
        width: 18,
        height: 18,
        padding: 'unset',
        paddingRight: 16,
        "& > .MuiSvgIcon-root": {
            fill: '#64748b',
        }
    },
    cancelButton: {
        borderRadius: 40,
        borderColor: theme.colors.neutral['100'],
        background: theme.colors.neutral['100'],
        color: theme.colors.neutral['500'],
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
    },
    saveButton: {
        borderRadius: 40,
        background: 'linear-gradient(90deg, #17b7a5 0%, #0e9486 100%)',
        color: '#fff !important',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
        '&:hover': {
            backgroundColor: theme.colors.primary['500'],
        }
    },
}));

const CommentaryContentCard: React.FC<{
    dashboard: Dashboard,
    newCommentary?: boolean,
    onClose: () => void,
}> = ({ dashboard, newCommentary, onClose }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const { setDashboards } = useContext(DashboardsContext);
    const { isPublicView } = useContext(DashboardContext);

    const [editable, setEditable] = useState<boolean>(false);
    const [saveReady, setSaveReady] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const inputValue = useRef<TextFieldProps>(null);

    const commentaryNote = useMemo(() => dashboard!.notes?.find(note => note.title === CommentaryTitle), [dashboard]);
    const commentaryNoteContent = useMemo(() => (JSON.parse(commentaryNote?.data || '{}') as NotesContent[])?.[1]?.data?.text || '', [commentaryNote]);

    const handleSave = useCallback(async (commentary: string) => {
        setLoading(true);

        const noteData = JSON.stringify([
            {type: 'header', data: {level: 2,  text: CommentaryTitle,}},
            {type: 'paragraph', data: {text: commentary}},
        ]);
        const newNote: DashboardNote = {
            id: uuidv4(),
            title: CommentaryTitle,
            data: noteData,
            type: DashboardNoteType.External,
            createdBy: getUserEmail(user),
            updatedBy: getUserEmail(user),
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        let oldNotes: DashboardNote[] = dashboard!.notes || [];
        let newNotes: DashboardNote[] = [];

        if (commentaryNote) {
            newNotes = oldNotes.map(note => {
                if (note.id === commentaryNote.id)
                    return newNote;
                return note;
            }) || [];
        } else {
            newNotes = [...oldNotes, newNote];
        }

        const dashboardUpdate: Dashboard = {
            ...dashboard!,
            notes: newNotes,
            refreshData: {
                lastUpdatedBy: 'user',
                shouldRefresh: true,
                modifiedData: RefreshDataEnum.Notes,
            }
        };

        updateDashboardFunc(dashboardUpdate).then(() => {
            setDashboards(prev => prev.map(d => d.id === dashboard.id ? dashboardUpdate : d));
            setLoading(false);
            setSaveReady(false);
            setEditable(false);
        });
    // eslint-disable-next-line
    }, [user, dashboard, commentaryNote]);

    useEffect(() => {
        if (newCommentary) {
            setEditable(true);
            setSaveReady(true);
        }
    // eslint-disable-next-line
    }, []);

    return (<>
        <Stack direction="column" className={classes.card} spacing={2} justifyContent="stretch">
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" width="100%">
                <Typography className={classes.title}>
                    {`Commentary`}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-end">
                    {(!editable && !isPublicView) && (
                        <IconButton className={classes.icon} disableRipple
                            onClick={() => setEditable(true)}>
                            <EditOutlinedIcon />
                        </IconButton>
                    )}
                    <IconButton className={classes.icon} disableRipple
                        onClick={() => onClose()}>
                        <CloseIcon fontSize="small" sx={{ color: '#64748b' }} />
                    </IconButton>
                </Stack>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" width="100%">
                <Stack direction="column" spacing={2} alignItems="flex-start" width="100%">
                    <Stack className={classes.container} width="100%"
                        sx ={editable ? { border: `1px solid ${theme.colors.neutral['300']}` } : {}}>
                        <TextField variant="outlined"
                            className={classNames('no-border', classes.inputField)}
                            inputProps={{ style: { width: '100%' }, className: 'no-fade' }}
                            defaultValue={commentaryNoteContent}
                            placeholder={!isPublicView ? 'Add your comments here...' : ''}
                            onChange={() => setSaveReady(!!inputValue.current?.value)}
                            inputRef={inputValue}
                            disabled={isPublicView || !editable}
                            minRows={3}
                            maxRows={3}
                            multiline
                        />
                    </Stack>
                    {editable && (<>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" width="100%">
                            <Button variant="outlined" size="small"
                                className={classes.cancelButton}
                                onClick={() => setEditable(false)}
                            > {'Cancel'} </Button>
                            <LoadingButton variant="contained" size="small"
                                className={classes.saveButton}
                                loading={loading}
                                disabled={!saveReady}
                                onClick={() => handleSave?.(inputValue.current?.value as string)}
                            > {'Save'} </LoadingButton>
                        </Stack>
                    </>)}
                </Stack>
            </Stack>
        </Stack>
    </>);
};

export default memo(CommentaryContentCard);
